/**
 * Timesact Back-in-Stock (BIS) — theme drawer flow
 * - Loads /apps/timesact/config + shop settings
 * - When configBIS.status === "ACTIVE", swaps ATC for Notify me
 * - Opens x-drawer (same as BNPL / size guide) and POSTs to Timesact
 */
(() => {
  function defaultCopy() {
    const strings = window.themeVariables?.strings || {};
    return {
      heading: 'Notify me when available',
      description: 'We will send you a notification as soon as this product is available again.',
      note: "We respect your privacy and don't share your email with anybody.",
      newsletter: 'Subscribe to our newsletter and stay up to date on offers and promotions.',
      button: strings.notifyMeButton || 'Notify me',
      success: "You're on the list — we'll email you when this is back in stock.",
      error: 'Something went wrong. Please check your email and try again.',
    };
  }

  const cache = {
    variants: null,
    settings: null,
    templates: null,
    productId: null,
    loaded: false,
    loading: null,
  };

  function productRoot(form) {
    return form?.closest('product-form') || form;
  }

  function getProductId(form) {
    return productRoot(form)?.dataset?.productId || null;
  }

  function getProductTitle(form) {
    return productRoot(form)?.dataset?.productTitle || document.title;
  }

  function getDrawer(form) {
    const section = form.closest('.shopify-section--main-product');
    return section?.querySelector('[data-timesact-bis-drawer]') || null;
  }

  function getNotifyButton(form) {
    return form.querySelector('.timesact-button-bis, .product-cta__notify');
  }

  function getAtcButton(form) {
    return (
      form.querySelector('.product-cta__atc') ||
      form.querySelector('buy-buttons button[type="submit"]')
    );
  }

  function setButtonLabel(button, label) {
    if (!button || !label) return;
    const content = button.querySelector('button-content') || button;
    content.textContent = label;
    if (content.hasAttribute('rotated-text')) content.setAttribute('rotated-text', label);
    if (content.hasAttribute('up-text')) content.setAttribute('up-text', label);
  }

  async function fetchJson(url) {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Timesact request failed: ${response.status}`);
    return response.json();
  }

  async function loadTimesact(productId) {
    if (!productId) return null;
    if (cache.loaded && String(cache.productId) === String(productId)) {
      return cache;
    }
    if (cache.loading) return cache.loading;

    cache.loading = (async () => {
      try {
        const [config, shop, settings] = await Promise.all([
          fetchJson(`/apps/timesact/config?productId=${productId}`).catch(() => null),
          fetchJson('/apps/timesact/shop').catch(() => null),
          fetchJson('/apps/timesact/shop/settings').catch(() => null),
        ]);

        cache.productId = productId;
        cache.variants = config?.product?.variants || null;
        cache.settings = settings || shop?.settings || null;
        cache.templates = shop?.settings?.templates || settings?.templates || null;
        cache.loaded = Boolean(cache.variants);
        return cache;
      } catch (error) {
        console.warn('[timesact-bis]', error);
        cache.loaded = false;
        return cache;
      } finally {
        cache.loading = null;
      }
    })();

    return cache.loading;
  }

  function variantBisConfig(variantId) {
    if (!cache.variants || !variantId) return null;
    return cache.variants[variantId] || cache.variants[String(variantId)] || null;
  }

  function isBisActive(variantId) {
    const variant = variantBisConfig(variantId);
    if (!variant?.isBISEnabled) return false;
    return variant.configBIS?.status === 'ACTIVE';
  }

  function bisSettings(variantId) {
    const variant = variantBisConfig(variantId);
    const settingsMap = cache.settings || {};
    const defaultId = cache.templates?.BIS;
    const templateId = variant?.configBIS?.settingsTemplate;
    return (templateId && settingsMap[templateId]) || (defaultId && settingsMap[defaultId]) || null;
  }

  function applyDrawerCopy(drawer, settings) {
    if (!drawer) return;
    // Leave the submit CTA markup/label alone — same theme button as cart Checkout.
    // Only toggle optional newsletter from Timesact settings.
    const form = settings?.form || {};
    const newsletterWrap = drawer.querySelector('[data-bis-newsletter-wrap]');
    if (newsletterWrap) {
      const enabled = form.isNewsletterEnabled !== false;
      newsletterWrap.hidden = !enabled;
    }
  }

  function setBisUi(form, { active, buttonName }) {
    const atc = getAtcButton(form);
    const notify = getNotifyButton(form);
    const payment = form.querySelector('.shopify-payment-button');

    if (active) {
      if (atc) atc.setAttribute('data-bis-hidden', '');
      if (notify) {
        notify.setAttribute('data-bis-active', '');
        setButtonLabel(notify, buttonName || defaultCopy().button);
      }
      if (payment) payment.style.display = 'none';
    } else {
      if (atc) atc.removeAttribute('data-bis-hidden');
      if (notify) notify.removeAttribute('data-bis-active');
      if (payment) payment.style.display = '';
    }
  }

  async function sync(form, variant, { sizeSelected } = {}) {
    if (!form) return;

    const productId = getProductId(form);
    await loadTimesact(productId);

    const variantId = variant?.id || form.querySelector('[name="id"]')?.value;
    const ready = sizeSelected !== false && form.dataset.sizeSelected !== 'false';
    const active = Boolean(ready && variant && !variant.available && isBisActive(variantId));
    const settings = active ? bisSettings(variantId) : null;
    const buttonName = settings?.button?.name || defaultCopy().button;

    setBisUi(form, { active, buttonName });

    const drawer = getDrawer(form);
    if (drawer) {
      drawer.dataset.variantId = variantId ? String(variantId) : '';
      if (active) applyDrawerCopy(drawer, settings);
    }
  }

  function hideMessages(drawer) {
    drawer.querySelector('[data-bis-success]')?.setAttribute('hidden', '');
    drawer.querySelector('[data-bis-error]')?.setAttribute('hidden', '');
  }

  function showMessage(drawer, type) {
    hideMessages(drawer);
    const el = drawer.querySelector(type === 'success' ? '[data-bis-success]' : '[data-bis-error]');
    if (el) el.removeAttribute('hidden');
  }

  async function submitBis(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const drawer = formEl.closest('[data-timesact-bis-drawer]');
    if (!drawer) return;

    const emailInput = formEl.querySelector('input[type="email"], input[name="email"]');
    const newsletter = formEl.querySelector('input[name="newsletter"]');
    const submit = formEl.querySelector('[type="submit"]');
    const email = emailInput?.value?.trim();

    hideMessages(drawer);

    if (!email || !emailInput?.checkValidity?.()) {
      emailInput?.reportValidity?.();
      emailInput?.classList.add('is-invalid');
      return;
    }
    emailInput.classList.remove('is-invalid');

    const productId = drawer.dataset.productId;
    const variantId = drawer.dataset.variantId;
    const productName = drawer.dataset.productTitle || '';

    if (submit) submit.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/apps/timesact/notifications/bis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          type: 'EMAIL',
          status: 'PENDING',
          value: email,
          productId: Number(productId) || productId,
          variantId: Number(variantId) || variantId,
          productName,
          isSubNewsletter: Boolean(newsletter?.checked),
        }),
      });

      if (!response.ok) throw new Error('BIS subscribe failed');
      showMessage(drawer, 'success');
      formEl.reset();
    } catch (error) {
      console.warn('[timesact-bis]', error);
      showMessage(drawer, 'error');
    } finally {
      if (submit) submit.removeAttribute('aria-busy');
    }
  }

  function bindDrawer(drawer) {
    if (!drawer || drawer.dataset.bisBound === 'true') return;
    drawer.dataset.bisBound = 'true';
    const formEl = drawer.querySelector('[data-timesact-bis-form]');
    formEl?.addEventListener('submit', submitBis);

    drawer.addEventListener('dialog:after-hide', () => {
      hideMessages(drawer);
      formEl?.querySelector('input[type="email"]')?.classList.remove('is-invalid');
    });
  }

  async function initSection(section) {
    const form = section.querySelector('product-form form');
    if (!form) return;

    const productId = getProductId(form);
    await loadTimesact(productId);

    section.querySelectorAll('[data-timesact-bis-drawer]').forEach(bindDrawer);

    const variantId = form.querySelector('[name="id"]')?.value;
    const fromCta = form.querySelector('[data-cta-variants]');
    let variant = null;
    if (fromCta) {
      try {
        const list = JSON.parse(fromCta.textContent);
        variant = list.find((v) => String(v.id) === String(variantId)) || null;
      } catch (e) {
        /* ignore */
      }
    }

    await sync(form, variant || { id: variantId, available: true }, {
      sizeSelected: form.dataset.sizeSelected !== 'false',
    });
  }

  function initAll() {
    document.querySelectorAll('.shopify-section--main-product').forEach((section) => {
      initSection(section);
    });
  }

  // Public hook used by product-cta.js after CTA state updates
  window.NookieTimesactBis = {
    sync,
    isBisActive,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.closest?.('.shopify-section--main-product') || event.target;
    if (section?.classList?.contains('shopify-section--main-product')) {
      initSection(section);
    }
  });
})();
